import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { CookieBanner } from 'shared/src/CookieBanner';

import { initializeTracking } from 'utils/tracking';

import { ImpersonationBanner } from '@organisms/ImpersonationBanner';
import { NewsBanner } from '@organisms/NewsBanner';
import { Sidebar } from '@organisms/Sidebar/Sidebar';
import { TopBar } from '@organisms/TopBar';
import { SandboxTopBanner } from '@sandbox/SandboxTopBanner';
import {
  ContractedProductsProvider,
  OrganisationsProvider,
  VirtualAccountsProvider
} from 'contexts';

import { useMfaSettings } from 'hooks/useMfaSettings';
import { useRedirectTo } from 'hooks/useRedirectTo';

/**
 * AppLayout component that wraps the entire application.
 * Provides necessary context providers and renders the top bar, sidebar, and outlet.
 */
export const AppLayout = () => {
  const { totpEnabled, skipMfa } = useMfaSettings();

  const { navigateWithRedirectTo } = useRedirectTo();

  useEffect(() => {
    if (!totpEnabled && !skipMfa) {
      navigateWithRedirectTo('/mfa-setup');
    }
  }, [navigateWithRedirectTo, skipMfa, totpEnabled]);

  return (
    <OrganisationsProvider>
      <ContractedProductsProvider>
        <VirtualAccountsProvider>
          {/* This mutation observer can be removed when /src/components/ui/organisms/DataTable/DataTable.tsx is removed */}
          <div className="parent">
            <div className="topbar-grid">
              {/* The SandboxTopBanner is excluded from the 'prod' environment using the withSandbox HOC */}
              <SandboxTopBanner />
              {/* Show twilio verify migration banner */}
              <NewsBanner />
              {/* Show impersonation banner */}
              <ImpersonationBanner />
              <TopBar />
            </div>
            <div className="sidebar-grid">
              <Sidebar />
            </div>
            <div className="outlet-grid">
              <div className="flex min-w-0 flex-1 flex-col overflow-auto">
                <Outlet />
              </div>
              <div className="fixed bottom-4 right-0 flex w-full justify-center lg:bottom-8 lg:right-8 lg:w-[500px]">
                <CookieBanner
                  showRejection
                  onAcceptCookies={initializeTracking}
                />
              </div>
            </div>
          </div>
        </VirtualAccountsProvider>
      </ContractedProductsProvider>
    </OrganisationsProvider>
  );
};
